// SettingsIconPane.jsx

import React, { useRef, useState } from 'react';

// Context
import { useBase } from 'context/BaseContext';

// Utilities
import { PhotoShape } from 'utilities/Enums';

// Services
import { clearBaseIcon, uploadBaseIcon } from 'services/bases/BaseCoverService';

// Components
import SettingsModalPaneHeader from './SettingsModalPaneHeader';
import SettingsModalPaneContents from './SettingsModalPaneContents';
import Button from 'components/button/Button';
import BaseCover from 'components/basecover/BaseCover';

/**
 * SettingsIconPane
 *
 * Renders upload and remove controls for the active icon.
 *
 * @param {object} theme - Active theme tokens for dynamic styling.
 * @param {object} base - Active base model.
 * @param {boolean} canEditIcon - Whether the current user can edit icon.
 *
 * @returns {ReactNode} The rendered icon settings pane.
 */
const SettingsIconPane = React.memo(({
    theme,
    base,
    canEditIcon,
}) => {

    // ----- CONTEXT ----- //

    const { setActiveBase } = useBase();

    // ----- REFS ----- //

    const fileInputRef = useRef(null);
    // ----- STATE ----- //

    const [isUploading, setIsUploading] = useState(false);
    const [isRemoving, setIsRemoving] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [statusMessage, setStatusMessage] = useState('');

    const hasIcon = Boolean(
        String(base?.iconPhotoUrl || base?.coverPhotoUrl || '').trim()
    );

    // ----- HANDLERS ----- //

    /**
 * Handles choose click.
 */

    const handleChooseClick = () => {
        if (!canEditIcon || isUploading || isRemoving) return;
        fileInputRef.current?.click();
    };

    /**
 * Handles file change.
 *
 * @param {*} event
 */
    const handleFileChange = async (event) => {
        const file = event.target?.files?.[0] || null;
        event.target.value = '';

        if (!file || !base?.key) return;

        setErrorMessage('');
        setStatusMessage('Uploading base icon...');
        setIsUploading(true);

        try {
            const nextIconPhotoUrl = await uploadBaseIcon({
                baseKey: base.key,
                file,
                previousIconPath: base.iconPhotoUrl || base.coverPhotoUrl || null,
            });

            setActiveBase((previous) => {
                if (!previous || previous.key !== base.key) return previous;
                return {
                    ...previous,
                    iconPhotoUrl: nextIconPhotoUrl,
                };
            });

            setStatusMessage('Icon updated.');
        } catch (error) {
            setErrorMessage(error?.message || 'Failed to upload icon.');
            setStatusMessage('');
        } finally {
            setIsUploading(false);
        }
    };

    /**
 * Handles remove click.
 */
    const handleRemoveClick = async () => {
        if (!canEditIcon || !base?.key || !base?.iconPhotoUrl || isUploading || isRemoving) return;

        setErrorMessage('');
        setStatusMessage('Removing icon...');
        setIsRemoving(true);

        try {
            await clearBaseIcon({
                baseKey: base.key,
                previousIconPath: base.iconPhotoUrl || null,
            });

            setActiveBase((previous) => {
                if (!previous || previous.key !== base.key) return previous;
                return {
                    ...previous,
                    iconPhotoUrl: null,
                };
            });

            setStatusMessage('Icon removed.');
        } catch (error) {
            setErrorMessage(error?.message || 'Failed to remove icon.');
            setStatusMessage('');
        } finally {
            setIsRemoving(false);
        }
    };

    // ----- RENDER ----- //

    return (
        <>
            <SettingsModalPaneHeader
              title="Icon"
              subtitle="Icon used in the switcher, list rows, and compact headers."
            />

            <SettingsModalPaneContents>
                <div
                  className="settings-modal-cover-card"
                  style={{
                    borderColor: theme.backgroundHover,
                  }}
                >
                    <div className="settings-modal-cover-preview">
                        <BaseCover
                          base={base}
                          assetType="icon"
                          borderWidth={0}
                          borderRadius={10}
                          size={80}
                          shape={PhotoShape.SQUARE}
                        />
                    </div>

                    <div className="settings-modal-cover-meta">
                        <div
                          className="settings-modal-setting-label"
                          style={{
                            color: theme.foreground,
                          }}
                        >
                            Icon
                        </div>
                        <div
                          className="settings-modal-setting-description"
                          style={{
                            color: theme.foregroundMuted,
                          }}
                        >
                            Images are center-cropped to a square and optimized for compact identity surfaces.
                        </div>
                    </div>

                    <div className="settings-modal-cover-actions">
                        <Button
                          iconKey={hasIcon ? 'image' : 'upload'}
                          label={hasIcon ? 'Replace' : 'Upload'}
                          enabled={canEditIcon && !isUploading && !isRemoving}
                          onClick={handleChooseClick}
                        />

                        <Button
                          iconKey="delete"
                          label="Remove"
                          enabled={canEditIcon && Boolean(base?.iconPhotoUrl) && !isUploading && !isRemoving}
                          onClick={handleRemoveClick}
                        />
                    </div>
                </div>

                {!canEditIcon && (
                    <div
                      className="settings-modal-note"
                      style={{
                        color: theme.foregroundMuted,
                      }}
                    >
                        Only admins and owners can update the icon.
                    </div>
                )}

                {(isUploading || isRemoving || statusMessage) && !errorMessage && (
                    <div className="settings-modal-status">{statusMessage}</div>
                )}

                {errorMessage && (
                    <div className="settings-modal-error">{errorMessage}</div>
                )}

                <input
                  style={{
                    display: 'none',
                  }}
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                />
            </SettingsModalPaneContents>
        </>
    );
});

// Dev Tools
SettingsIconPane.displayName = 'SettingsIconPane';

export default SettingsIconPane;
